import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import TagReference from '../components/Templates/TagReference';
import TemplateList from '../components/Templates/TemplateList';
import TemplatePreviewDialog from '../components/Templates/TemplatePreviewDialog';
import TemplateUploader from '../components/Templates/TemplateUploader';
import { TEMPLATE_CONFIGS } from '../config/templateConfig';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  deleteUserTemplate,
  downloadTemplate,
  getAllTemplates,
  getTemplatePreviewContent,
  setTemplateAsDefault,
  uploadClientTemplate,
  uploadUserTemplate,
} from '../services/templateService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Template, TemplateType } from '../types/template';

const TemplatePage: React.FC = () => {
  const theme = useTheme();
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState<TemplateType>('draft');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingTemplateId, setDownloadingTemplateId] = useState<string | null>(null);

  // Preview State
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | undefined>(undefined);
  const [previewType, setPreviewType] = useState<TemplateType>('draft');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Permission Check
  const canManageClient = hasPermission(PERMISSIONS.TEMPLATE_MANAGE_CLIENT);
  const canManageTeam = canManageClient || hasPermission(PERMISSIONS.TEMPLATE_UPLOAD_TEAM);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchTemplates = useCallback(async () => {
    if (!account?.uid || !account?.clientId) {
      return;
    }
    setLoading(true);
    try {
      const data = await getAllTemplates(account.uid, account.clientId, currentTab);
      setTemplates(data);
    } catch (_error) {
      showSnackbar('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [account?.uid, account?.clientId, currentTab, showSnackbar]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TemplateType) => {
    setCurrentTab(newValue);
  };

  const handleUpload = async (file: File, validTags: string[], scope: 'personal' | 'company') => {
    if (!account?.uid || !account?.clientId) {
      showSnackbar('User context missing', 'error');
      return;
    }
    // Fallback for teamId if not on account object directly (depending on AuthContext shape)
    const teamId = account.teamId || 'default_team';

    try {
      if (scope === 'company') {
        await uploadClientTemplate(account.uid, account.clientId, file, currentTab, validTags);
      } else {
        await uploadUserTemplate(
          account.uid,
          account.clientId,
          teamId,
          file,
          currentTab,
          validTags
        );
      }
      showSnackbar('Template uploaded successfully', 'success');
      fetchTemplates();
    } catch (error) {
      showSnackbar('Failed to upload template', 'error');
      throw error; // Propagate to uploader to stop loading state
    }
  };

  const handleDelete = async (template: Template) => {
    if (!account?.uid) {
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${template.name}?`)) {
      try {
        await deleteUserTemplate(account.uid, template);
        showSnackbar('Template deleted', 'success');
        fetchTemplates();
      } catch (_error) {
        showSnackbar('Failed to delete template', 'error');
      }
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const dataUrlToBlob = (dataUrl: string): Blob => {
    const [header = '', data = ''] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(data);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleDownload = async (template: Template) => {
    const filename = template.originalFilename || template.name;
    setDownloadingTemplateId(template.id);

    try {
      // Strategy 1: Unified download with Client-Side Decryption support
      const blob = await downloadTemplate(template);
      downloadBlob(blob, filename);
    } catch (_error) {
      // Strategy 2: Proxy via Cloud Function (Base64) - Backup
      try {
        const dataUrl = await getTemplatePreviewContent(template.storagePath);
        if (dataUrl) {
          downloadBlob(dataUrlToBlob(dataUrl), filename);
          return;
        }
      } catch (_innerError) {
        showSnackbar('Failed to download template', 'error');
      }
    } finally {
      setDownloadingTemplateId(null);
    }
  };

  const handleSetDefault = async (template: Template, scope: 'personal' | 'team' | 'company') => {
    if (!account?.uid || !account?.clientId) {
      return;
    }
    const teamId = account.teamId || 'default_team';

    try {
      await setTemplateAsDefault(account.uid, account.clientId, teamId, template, scope);
      const scopeLabel = scope === 'personal' ? 'My' : scope === 'team' ? 'Team' : 'Company';
      showSnackbar(`${scopeLabel} default template updated`, 'success');
      fetchTemplates();
    } catch (_error) {
      showSnackbar('Failed to set default template', 'error');
    }
  };

  const handlePreview = async (template: Template) => {
    setIsPreviewLoading(true);
    try {
      // Use the new Cloud Function based preview fetcher to avoid CORS
      const dataUrl = await getTemplatePreviewContent(template.storagePath);

      // Convert Data URL to File object
      const arr = dataUrl.split(',');
      if (arr.length < 2) {
        throw new Error('Invalid data URL received from server');
      }

      const header = arr[0] || '';
      const data = arr[1] || '';

      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch
        ? mimeMatch[1]
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      const bstr = atob(data);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      // Ensure mime is treated as string (it is, but TS might be strict about the fallback return type of match[1])
      const file = new File([u8arr], template.name, { type: mime || 'application/octet-stream' });

      setPreviewFile(file);
      setPreviewFileName(template.name);
      setPreviewType(template.type);
      setShowPreview(true);
    } catch (_error) {
      showSnackbar('Failed to load preview. Try downloading instead.', 'error');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  return (
    <PageContainer title="" maxWidth={false}>
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
              >
                Template Management
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: alpha(theme.palette.bidlogiq.brick, 0.08),
            color: theme.palette.text.primary,
            border: `1px solid ${alpha(theme.palette.bidlogiq.brick, 0.3)}`,
            '& .MuiAlert-icon': { color: theme.palette.bidlogiq.brick },
          }}
        >
          <AlertTitle
            sx={{ color: theme.palette.bidlogiq.brick, fontWeight: 'bold', fontSize: '1.1rem' }}
          >
            How it works
          </AlertTitle>
          <Typography variant="body1" component="div">
            Add your own branding in 4 easy steps:
            <ol style={{ margin: '12px 0', paddingLeft: '24px' }}>
              <li>Start with an example template below or use your own Word (.docx) document.</li>
              <li>
                Copy the <strong>Tags</strong> from the bottom of this page and paste them into your
                document where you want the content to appear.
              </li>
              {canManageClient ? (
                <li>
                  Upload your file as a <strong>Personal Template</strong> (just for you) or a{' '}
                  <strong>Company Template</strong> (for everyone). The system will check that all
                  required tags are included.
                </li>
              ) : (
                <li>
                  Upload your file below. The system will check that all required tags are included.
                </li>
              )}
              <li>
                Set your new template as your <strong>Personal Default</strong>
                {canManageClient ? (
                  <>
                    , <strong>Team Default</strong>, or <strong>Company Default</strong>.
                  </>
                ) : canManageTeam ? (
                  <>
                    {' '}
                    or <strong>Team Default</strong>.
                  </>
                ) : (
                  <>.</>
                )}
              </li>
            </ol>
          </Typography>
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="template types"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: (theme) => theme.palette.bidlogiq.brick,
              },
            }}
          >
            <Tab
              label="Draft Templates"
              value="draft"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&.Mui-selected': { color: (theme) => theme.palette.bidlogiq.brick },
              }}
            />
            <Tab
              label="CV Templates"
              value="cv"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&.Mui-selected': { color: (theme) => theme.palette.bidlogiq.brick },
              }}
            />
            {/* <Tab label="Case Study Templates" value="case_study" /> */}
          </Tabs>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' }, // Stack on mobile/tablet, row on desktop (lg)
            gap: 3,
          }}
        >
          {/* Main Content Area (List + Uploader) */}
          <Box sx={{ flex: 2, overflowX: 'hidden' }}>
            <Typography variant="h6" gutterBottom>
              {TEMPLATE_CONFIGS[currentTab]?.label}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
              {TEMPLATE_CONFIGS[currentTab]?.description}
            </Typography>

            {currentTab === 'draft' && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  backgroundColor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: theme.palette.info.main }}
                >
                  How to create a Draft Template
                </Typography>
                <Typography variant="body1" paragraph>
                  Draft templates use simple tags to tell the system where to put your generated
                  answers.{' '}
                  <Link
                    href="#tag-reference"
                    sx={{ color: theme.palette.info.main, fontWeight: 'bold' }}
                  >
                    Check the tag reference examples at the bottom of this page.
                  </Link>
                </Typography>
                <ol
                  style={{
                    paddingLeft: '24px',
                    margin: '12px 0',
                    fontSize: '1rem',
                    marginBottom: '24px',
                  }}
                >
                  <li style={{ marginBottom: '12px' }}>
                    <strong>Required Tags:</strong> Copy and paste exact tags like{' '}
                    <strong>INSERT DRAFT SECTIONS</strong> so the system knows where to insert your
                    proposal content.
                  </li>
                  <li style={{ marginBottom: '12px' }}>
                    <strong>Custom Instructions:</strong> You can also type natural instructions
                    (like "INSERT an executive summary here") for the AI to follow.
                  </li>
                  <li>
                    <strong>Formatting:</strong> Make sure to format your tags! The generated
                    document will keep whatever font, size, and color you apply to the tags in Word.
                  </li>
                </ol>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/system_response_template.docx';
                    link.download = 'system_response_template.docx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  sx={{
                    backgroundColor: theme.palette.greyContrast,
                    color: theme.palette.background.default,
                    border: `2px solid ${theme.palette.bidlogiq.orange}`,
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: theme.palette.greyContrast,
                      opacity: 0.9,
                    },
                  }}
                >
                  Download Example Draft
                </Button>
              </Paper>
            )}

            {currentTab === 'cv' && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  backgroundColor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: theme.palette.info.main }}
                >
                  How to create a CV Template
                </Typography>
                <Typography variant="body1" paragraph>
                  CVs are generated by replacing tags in your template with real data. The AI reads
                  your document and looks for specific keywords.{' '}
                  <Link
                    href="#tag-reference"
                    sx={{ color: theme.palette.info.main, fontWeight: 'bold' }}
                  >
                    Check the tag reference examples at the bottom of this page.
                  </Link>
                </Typography>
                <ol
                  style={{
                    paddingLeft: '24px',
                    margin: '12px 0',
                    fontSize: '1rem',
                    marginBottom: '24px',
                  }}
                >
                  <li style={{ marginBottom: '12px' }}>
                    <strong>The "INSERT" Keyword:</strong> Type "INSERT" followed by what you want
                    the AI to create (e.g., "INSERT Career history, up to 3 entries" or "INSERT bio,
                    200 words").
                  </li>
                  <li>
                    <strong>The "EXAMPLE" Keyword:</strong> If you want a specific layout (like a
                    list of past projects), write <strong>EXAMPLE:</strong> right after your{' '}
                    <strong>INSERT</strong> instruction, and then type out a mockup of how it should
                    look.
                  </li>
                </ol>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/system_cv_template.docx';
                    link.download = 'system_cv_template.docx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  sx={{
                    backgroundColor: theme.palette.greyContrast,
                    color: theme.palette.background.default,
                    border: `2px solid ${theme.palette.bidlogiq.orange}`,
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: theme.palette.greyContrast,
                      opacity: 0.9,
                    },
                  }}
                >
                  Download Example CV
                </Button>
              </Paper>
            )}

            <TemplateUploader
              type={currentTab}
              onUpload={handleUpload}
              canManageClient={canManageClient}
            />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TemplateList
                templates={templates}
                onDelete={handleDelete}
                onDownload={handleDownload}
                onSetDefault={handleSetDefault}
                onPreview={handlePreview}
                downloadingTemplateId={downloadingTemplateId}
                canManageClient={canManageClient}
                canManageTeam={canManageTeam}
              />
            )}

            <Box
              sx={{
                mt: 4,
                p: 3,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                textAlign: 'center',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Need help setting up your templates?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                We offer <strong>free 1:1 support</strong> to help you integrate your branding and
                get your templates working perfectly.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontStyle: 'italic' }}
              >
                Need custom fields or specific template sections? Just get in touch and we can
                configure them for you.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.bidlogiq.brick, fontWeight: 'bold' }}
              >
                <a
                  href="mailto:support@bidlogiq.com"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  support@bidlogiq.com
                </a>
              </Typography>{' '}
              <Typography
                variant="body2"
                sx={{ mt: 1, color: theme.palette.bidlogiq.brick, fontWeight: 'bold' }}
              >
                +61 (0)7 2143 6035
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tag Reference below TemplateList */}
        {TEMPLATE_CONFIGS[currentTab] && (
          <Box sx={{ mt: 4 }} id="tag-reference">
            <TagReference config={TEMPLATE_CONFIGS[currentTab]} type={currentTab} />
          </Box>
        )}

        {/* Global Preview Dialog for existing templates */}
        {isPreviewLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0,0,0,0.3)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <TemplatePreviewDialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          file={previewFile}
          fileName={previewFileName || ''}
          templateType={previewType}
        />
      </Box>
    </PageContainer>
  );
};

export default TemplatePage;
